import React, { useContext, useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { MapPin, Users, Award, Target, Truck, Shield } from 'lucide-react';
import { 
  EditModeProvider, 
  EditModeContext, 
  AdminToolbar, 
  EditableText, 
  EditableImage 
} from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AboutPageContent() {
  const { pageContent, setPageContent, editMode } = useContext(EditModeContext);
  const [siteImages, setSiteImages] = useState({});

  useEffect(() => {
    axios.get(`${API}/site-images`).then(r => setSiteImages(r.data || {})).catch(() => {});
  }, []);

  // Helper to get block content by type
  const getBlockContent = (type, defaultContent = {}) => {
    const block = pageContent?.blocks?.find(b => b.type === type);
    return block?.content || defaultContent;
  };

  // Helper to update block content
  const updateBlockContent = (type, updates) => {
    if (!pageContent) return;
    const blockIndex = pageContent.blocks?.findIndex(b => b.type === type);
    if (blockIndex >= 0) {
      const newBlocks = [...pageContent.blocks];
      newBlocks[blockIndex] = { 
        ...newBlocks[blockIndex], 
        content: { ...newBlocks[blockIndex].content, ...updates } 
      };
      setPageContent({ ...pageContent, blocks: newBlocks });
    } else {
      // Add block if it doesn't exist
      const newBlock = { id: `block-${Date.now()}`, type, content: updates };
      setPageContent({ ...pageContent, blocks: [...(pageContent?.blocks || []), newBlock] });
    }
  };

  const heroContent = getBlockContent('hero', {
    title: 'About',
    title_accent: 'Tech Distribution',
    subtitle: "Australia's trusted distributor for industrial networking, solar controllers, power systems, and outdoor enclosures since 2015.",
  });

  const storyContent = getBlockContent('text', {
    title: 'Our Story',
    paragraphs: [
      'Tech Distribution was founded with a simple mission: to provide Australian businesses with reliable, high-quality industrial technology products backed by exceptional service and technical expertise.',
      'What started as a small operation servicing local telecommunications contractors has grown into a nationwide distribution network, supplying everything from 60GHz wireless equipment to climate-controlled server cabinets.',
      'Today, we partner with leading manufacturers like Tachyon Networks to bring cutting-edge technology to installers, integrators, and enterprises across Australia. Our commitment to quality and service remains unchanged.',
    ],
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600',
    stat_value: '10+',
    stat_label: 'Years in Business',
  });

  const valuesContent = getBlockContent('features', {
    title: 'What Sets Us Apart',
    items: [
      { icon: 'award', title: 'Quality Products', description: 'We only stock products from trusted manufacturers with proven track records in demanding Australian conditions.' },
      { icon: 'users', title: 'Expert Support', description: "Our technical team has hands-on experience with every product we sell. We're here to help you spec the right solution." },
      { icon: 'truck', title: 'Fast Delivery', description: 'Sydney-based warehouse with same-day dispatch on most orders. We understand that downtime costs money.' },
    ],
  });

  const statsContent = getBlockContent('stats', {
    items: [
      { value: '500+', label: 'Products in Stock' },
      { value: '2,000+', label: 'Happy Customers' },
      { value: '24hr', label: 'Average Dispatch' },
      { value: '98%', label: 'Satisfaction Rate' },
    ],
  });

  const industriesContent = getBlockContent('industries', {
    title: 'Industries We Serve',
    items: ['Telecommunications', 'Solar & Renewables', 'Mining & Resources', 'Data Centers', 'Broadcasting', 'Government'],
  });

  const missionContent = getBlockContent('mission', {
    title: 'Our Mission',
    quote: '"To empower Australian businesses with reliable industrial technology solutions, backed by expert knowledge and uncompromising service."',
  });

  return (
    <Layout>
      <div data-testid="about-page">
        {/* Hero */}
        <section
          className="relative py-20 bg-zinc-900"
          style={(() => {
            const img = siteImages.about_hero?.url;
            const bgUrl = img ? (img.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img) : '';
            return bgUrl ? { backgroundImage: `url(${bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {};
          })()}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tight mb-6">
                <EditableText
                  value={heroContent.title || 'About'}
                  onChange={(val) => updateBlockContent('hero', { ...heroContent, title: val })}
                  tag="span"
                />
                {' '}
                <EditableText
                  value={heroContent.title_accent || 'Tech Distribution'}
                  onChange={(val) => updateBlockContent('hero', { ...heroContent, title_accent: val })}
                  tag="span"
                  className="text-[#78BE20]"
                />
              </h1>
              <EditableText
                value={heroContent.subtitle || "Australia's trusted distributor for industrial networking..."}
                onChange={(val) => updateBlockContent('hero', { ...heroContent, subtitle: val })}
                tag="p"
                className="text-xl text-zinc-300"
                multiline
              />
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <EditableText
                  value={storyContent.title || 'Our Story'}
                  onChange={(val) => updateBlockContent('text', { ...storyContent, title: val })}
                  tag="h2"
                  className="text-2xl sm:text-3xl font-black text-foreground uppercase tracking-tight mb-6"
                />
                <div className="space-y-4 text-muted-foreground">
                  {(storyContent.paragraphs || []).map((p, i) => (
                    <EditableText
                      key={i}
                      value={p}
                      onChange={(val) => {
                        const newParagraphs = [...(storyContent.paragraphs || [])];
                        newParagraphs[i] = val;
                        updateBlockContent('text', { ...storyContent, paragraphs: newParagraphs });
                      }}
                      tag="p"
                      multiline
                    />
                  ))}
                </div>
              </div>
              <div className="relative">
                <EditableImage
                  src={storyContent.image || 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600'}
                  onChange={(val) => updateBlockContent('text', { ...storyContent, image: val })}
                  alt="Tech Distribution warehouse"
                  className="rounded-sm shadow-xl w-full"
                />
                <div className="absolute -bottom-6 -left-6 bg-[#78BE20] text-black p-6 rounded-sm">
                  <EditableText
                    value={storyContent.stat_value || '10+'}
                    onChange={(val) => updateBlockContent('text', { ...storyContent, stat_value: val })}
                    tag="p"
                    className="text-3xl font-black"
                  />
                  <EditableText
                    value={storyContent.stat_label || 'Years in Business'}
                    onChange={(val) => updateBlockContent('text', { ...storyContent, stat_label: val })}
                    tag="p"
                    className="text-sm font-medium uppercase"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 bg-zinc-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <EditableText
              value={valuesContent.title || 'What Sets Us Apart'}
              onChange={(val) => updateBlockContent('features', { ...valuesContent, title: val })}
              tag="h2"
              className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight text-center mb-12"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {(valuesContent.items || []).map((item, i) => {
                const IconComponent = item.icon === 'award' ? Award : item.icon === 'users' ? Users : Truck;
                return (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 rounded-sm p-6 text-center">
                    <div className="w-14 h-14 bg-[#78BE20]/10 rounded-sm flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-7 w-7 text-[#78BE20]" />
                    </div>
                    <EditableText
                      value={item.title}
                      onChange={(val) => {
                        const newItems = [...valuesContent.items];
                        newItems[i] = { ...newItems[i], title: val };
                        updateBlockContent('features', { ...valuesContent, items: newItems });
                      }}
                      tag="h3"
                      className="text-lg font-bold text-white mb-2"
                    />
                    <EditableText
                      value={item.description}
                      onChange={(val) => {
                        const newItems = [...valuesContent.items];
                        newItems[i] = { ...newItems[i], description: val };
                        updateBlockContent('features', { ...valuesContent, items: newItems });
                      }}
                      tag="p"
                      className="text-zinc-400 text-sm"
                      multiline
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-[#78BE20]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {(statsContent.items || []).map((stat, i) => (
                <div key={i}>
                  <EditableText
                    value={stat.value}
                    onChange={(val) => {
                      const newItems = [...statsContent.items];
                      newItems[i] = { ...newItems[i], value: val };
                      updateBlockContent('stats', { items: newItems });
                    }}
                    tag="p"
                    className="text-4xl font-black text-black"
                  />
                  <EditableText
                    value={stat.label}
                    onChange={(val) => {
                      const newItems = [...statsContent.items];
                      newItems[i] = { ...newItems[i], label: val };
                      updateBlockContent('stats', { items: newItems });
                    }}
                    tag="p"
                    className="text-black/70 font-medium"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <EditableText
              value={industriesContent.title || 'Industries We Serve'}
              onChange={(val) => updateBlockContent('industries', { ...industriesContent, title: val })}
              tag="h2"
              className="text-2xl sm:text-3xl font-black text-foreground uppercase tracking-tight text-center mb-12"
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {(industriesContent.items || []).map((industry, i) => (
                <div 
                  key={i} 
                  className="bg-card border border-border rounded-sm p-4 text-center hover:border-[#78BE20] transition-colors"
                >
                  <EditableText
                    value={industry}
                    onChange={(val) => {
                      const newItems = [...industriesContent.items];
                      newItems[i] = val;
                      updateBlockContent('industries', { ...industriesContent, items: newItems });
                    }}
                    tag="p"
                    className="text-foreground font-medium text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 bg-zinc-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Target className="h-12 w-12 text-[#78BE20] mx-auto mb-6" />
            <EditableText
              value={missionContent.title || 'Our Mission'}
              onChange={(val) => updateBlockContent('mission', { ...missionContent, title: val })}
              tag="h2"
              className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-6"
            />
            <EditableText
              value={missionContent.quote || '"To empower Australian businesses..."'}
              onChange={(val) => updateBlockContent('mission', { ...missionContent, quote: val })}
              tag="p"
              className="text-xl text-zinc-300"
              multiline
            />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-black text-foreground uppercase tracking-tight mb-4">
              Ready to Work With Us?
            </h2>
            <p className="text-muted-foreground mb-8">
              Whether you're a first-time buyer or looking to set up a trade account, 
              we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/products" className="btn-primary inline-block text-center">
                Browse Products
              </a>
              <a href="/contact" className="btn-secondary inline-block text-center">
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </div>

      {/* Additional CMS Blocks */}
      <CMSBlockRenderer excludeTypes={['hero', 'text', 'features', 'stats', 'banner']} />

      {/* Admin Toolbar */}
      <AdminToolbar />
    </Layout>
  );
}

export default function AboutPage() {
  return (
    <EditModeProvider pageSlug="about">
      <AboutPageContent />
    </EditModeProvider>
  );
}
